// Polymarket US feed: owns the socket, books, account poll, market index, and BBO cache (display-only).

import { KalshiWs as VenueWs, type WsStatus } from '../kalshi/ws';
import { logWarn } from '../core/log';
import { dollarsToCash } from '../core/money';
import {
  type PmBalance, type PmBbo, type PmBook, type PmEvent, type PmExecution, type PmFill, type PmMarket, type PmOrder, type PmPosition,
  type PmResolution,
  PolymarketClient, parseBook, parseExecution, parseOrder, parsePositionDelta,
} from './client';
import { type PmMarketLedger, buildLedger } from './ledger';

export interface PmAccount {
  balance: PmBalance | null;
  positions: PmPosition[];
  orders: PmOrder[];
  /** Realised P&L, fees and fills per market, from our own trade history. */
  ledger: Map<string, PmMarketLedger>;
  /** The trade history the ledger was built from, kept so it can be split by account. */
  history: { fills: PmFill[]; resolutions: PmResolution[] };
  /** ms of the last successful poll; 0 before the first. */
  at: number;
  error: string | null;
}

export interface PmFeedEvents {
  account: PmAccount;
  book: PmBook;
  bbo: PmBbo;
  /** One order execution from the private stream, already applied to `account`. */
  execution: PmExecution;
  status: { ws: WsStatus; detail?: string };
  /** The private (account) socket's state. */
  privateStatus: { ws: WsStatus; detail?: string };
}

type Listener<K extends keyof PmFeedEvents> = (payload: PmFeedEvents[K]) => void;

const ACCOUNT_POLL_MS = 30_000;
const BBO_POLL_MS = 30_000;
/** A subscribed market may go this long without a socket frame before a disagreeing REST price means the socket is dead. */
const SILENT_BOOK_MS = 45_000;
/** A BBO shown in a search dropdown may be this stale before it is refetched. */
const BBO_TTL_MS = 10 * 60_000;
/** Search hits are cached per query; games gain markets over a day, not a minute. */
const SEARCH_TTL_MS = 5 * 60_000;
const SEARCH_CACHE_MAX = 40;

export class PolymarketFeed {
  readonly client = new PolymarketClient();
  account: PmAccount = { balance: null, positions: [], orders: [], ledger: new Map(), history: { fills: [], resolutions: [] }, at: 0, error: null };
  private ledgerTimer: number | null = null;
  private ledgerInFlight: Promise<void> | null = null;
  wsStatus: WsStatus = 'idle';
  privateWsStatus: WsStatus = 'idle';

  private ws: VenueWs | null = null;
  /** The authenticated account stream: orders, positions, balance. */
  private privateWs: VenueWs | null = null;
  private privateSeq = 0;
  private subs = new Map<string, number>();
  private books = new Map<string, PmBook>();
  private bbos = new Map<string, { at: number; bbo: PmBbo }>();
  /** When the socket last sent a book for each subscribed market (or when it was subscribed). */
  private lastFrameAt = new Map<string, number>();
  private bboInFlight = new Map<string, Promise<PmBbo | null>>();
  private searches = new Map<string, { at: number; events: PmEvent[] }>();
  private searchInFlight = new Map<string, Promise<PmEvent[]>>();
  /** Every market seen in any search hit or event fetch, for labels and tick sizes. */
  private known = new Map<string, { event: PmEvent; market: PmMarket }>();
  /** Every event seen in any search, by slug, so Explore can expand one without refetching. */
  private events = new Map<string, PmEvent>();
  private listeners = new Map<keyof PmFeedEvents, Set<Listener<any>>>();
  private timers: number[] = [];

  get running(): boolean {
    return this.ws !== null;
  }

  start(wsUrl: string, privateWsUrl?: string): void {
    if (this.ws) return;
    this.ws = new VenueWs({
      venue: 'polymarket',
      wsUrl,
      currentTickers: () => [...this.subs.keys()],
      onMessage: (m) => this.onFrame(m),
      onStatus: (ws, detail) => {
        this.wsStatus = ws;
        this.emit('status', { ws, detail });
      },
      // No private channels on this socket; the account is polled over REST.
      onOpenMessages: () => [],
      // The venue documents no heartbeat or request/reply command; silence is caught by the BBO poll instead.
      probeMessage: null,
      subscribeMessage: (slugs, nextId) => ({
        subscribe: {
          requestId: `md-${nextId()}`,
          subscriptionType: 'SUBSCRIPTION_TYPE_MARKET_DATA',
          marketSlugs: slugs,
        },
      }),
    });
    this.ws.start();
    if (privateWsUrl) this.startPrivate(privateWsUrl);
    void this.pollAccount();
    void this.refreshLedger();
    // With the stream up the poll is a safety net; without it, the source.
    this.timers = [
      window.setInterval(() => void this.pollAccount(), ACCOUNT_POLL_MS),
      window.setInterval(() => void this.pollBbos(), BBO_POLL_MS),
    ];
  }

  /** The private account socket: same signed handshake as the market one, but never subscribes per ticker. */
  private startPrivate(url: string): void {
    const path = new URL(url).pathname;
    this.privateWs = new VenueWs({
      venue: 'polymarket',
      wsPath: path,
      wsUrl: url,
      currentTickers: () => [],
      onMessage: (m) => this.onPrivateFrame(m),
      onStatus: (ws, detail) => {
        this.privateWsStatus = ws;
        this.emit('privateStatus', { ws, detail });
        // Reconnect replays the order snapshot; positions/balance don't, so re-read them once the stream is back.
        if (ws === 'open') void this.pollAccount();
      },
      probeMessage: null,
      onOpenMessages: () => ['SUBSCRIPTION_TYPE_ORDER_SNAPSHOT', 'SUBSCRIPTION_TYPE_ORDER', 'SUBSCRIPTION_TYPE_POSITION', 'SUBSCRIPTION_TYPE_ACCOUNT_BALANCE']
        .map((subscriptionType) => ({ subscribe: { requestId: `pv-${++this.privateSeq}`, subscriptionType } })),
      subscribeMessage: () => null,
    });
    this.privateWs.start();
  }

  stop(): void {
    this.ws?.stop();
    this.ws = null;
    this.privateWs?.stop();
    this.privateWs = null;
    for (const t of this.timers) clearInterval(t);
    this.timers = [];
    this.books.clear();
    this.account = { balance: null, positions: [], orders: [], ledger: new Map(), history: { fills: [], resolutions: [] }, at: 0, error: null };
    this.emit('account', this.account);
  }

  on<K extends keyof PmFeedEvents>(event: K, fn: Listener<K>): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(fn);
    return () => { set!.delete(fn); };
  }

  private emit<K extends keyof PmFeedEvents>(event: K, payload: PmFeedEvents[K]): void {
    for (const fn of this.listeners.get(event) ?? []) {
      try {
        fn(payload);
      } catch (e) {
        console.error('[pm-feed] listener error', e);
      }
    }
  }

  /** Refcounted; the socket subscribes on first panel while a REST snapshot fills the ladder meanwhile. */
  subscribe(slug: string): void {
    const n = (this.subs.get(slug) ?? 0) + 1;
    this.subs.set(slug, n);
    if (n === 1) this.lastFrameAt.set(slug, Date.now());
    if (n > 1) {
      const b = this.books.get(slug);
      if (b) this.emit('book', b);
      const q = this.bbos.get(slug);
      if (q) this.emit('bbo', q.bbo);
      return;
    }
    this.ws?.subscribeTickers([slug]);
    this.client.getBook(slug).then((b) => {
      // A socket frame may have landed first; never overwrite newer with older.
      const cur = this.books.get(slug);
      if (cur && cur.t > b.t) return;
      this.books.set(slug, b);
      this.emit('book', b);
    }).catch((e) => logWarn('pm-feed', `book snapshot for ${slug} failed: ${(e as Error).message}`));
    void this.bbo(slug, true);
  }

  release(slug: string): void {
    const n = (this.subs.get(slug) ?? 0) - 1;
    if (n > 0) {
      this.subs.set(slug, n);
      return;
    }
    this.subs.delete(slug);
    this.books.delete(slug);
    this.lastFrameAt.delete(slug);
    // No unsubscribe sent: dropped frames in onFrame, and reconnect only resubscribes live markets.
  }

  book(slug: string): PmBook | undefined {
    return this.books.get(slug);
  }

  /** Top of book + shares traded/open interest (the venue's only volume figure); cached, `fresh` forces a refetch. */
  async bbo(slug: string, fresh = false): Promise<PmBbo | null> {
    const cur = this.bbos.get(slug);
    if (cur && !fresh && Date.now() - cur.at < BBO_TTL_MS) return cur.bbo;
    const inflight = this.bboInFlight.get(slug);
    if (inflight) return inflight;
    const p = this.client.getBbo(slug).then((bbo) => {
      this.bbos.set(slug, { at: Date.now(), bbo });
      if (this.subs.has(slug)) this.emit('bbo', bbo);
      return bbo;
    }).catch((e) => {
      logWarn('pm-feed', `bbo for ${slug} failed: ${(e as Error).message}`);
      return cur?.bbo ?? null;
    }).finally(() => { this.bboInFlight.delete(slug); });
    this.bboInFlight.set(slug, p);
    return p;
  }

  cachedBbo(slug: string): PmBbo | null {
    return this.bbos.get(slug)?.bbo ?? null;
  }

  private async pollBbos(): Promise<void> {
    for (const slug of this.subs.keys()) {
      const q = await this.bbo(slug, true);
      if (q && this.wsStatus === 'open' && this.socketSilent(slug, q)) {
        this.ws?.restart(`no book frames for ${slug} in ${Math.round((Date.now() - (this.lastFrameAt.get(slug) ?? 0)) / 1000)}s while REST shows a different price`);
        return;
      }
    }
  }

  /** True when the socket has been quiet on a market whose REST top of book no longer matches what the socket last said. */
  private socketSilent(slug: string, q: PmBbo): boolean {
    const quietFor = Date.now() - (this.lastFrameAt.get(slug) ?? Date.now());
    if (quietFor < SILENT_BOOK_MS) return false;
    const b = this.books.get(slug);
    if (!b) return false;
    return (b.bids[0]?.priceMc ?? null) !== q.bidMc || (b.offers[0]?.priceMc ?? null) !== q.askMc;
  }

  /** Gateway event search, cached per query; a pasted URL or bare slug goes straight to the event endpoint. */
  async searchEvents(q: string, limit = 8): Promise<PmEvent[]> {
    const query = q.trim();
    if (!query) return [];
    const slug = slugFromQuery(query);
    const key = slug ? `slug:${slug}` : `${limit}:${query.toLowerCase()}`;
    const cached = this.searches.get(key);
    if (cached && Date.now() - cached.at < SEARCH_TTL_MS) return cached.events;
    const inflight = this.searchInFlight.get(key);
    if (inflight) return inflight;
    const p = (slug
      ? this.client.getEvent(slug).then((e) => [e]).catch(() => this.client.searchEvents(query, limit))
      : this.client.searchEvents(query, limit)
    ).then((events) => {
      for (const e of events) {
        this.events.set(e.slug, e);
        for (const m of e.markets) this.known.set(m.slug, { event: e, market: m });
      }
      this.searches.set(key, { at: Date.now(), events });
      if (this.searches.size > SEARCH_CACHE_MAX) this.searches.delete(this.searches.keys().next().value!);
      return events;
    }).finally(() => { this.searchInFlight.delete(key); });
    this.searchInFlight.set(key, p);
    return p;
  }

  eventBySlug(slug: string): PmEvent | null {
    return this.events.get(slug) ?? null;
  }

  /** What a market on screen is called, from whatever search or event fetch showed it. */
  marketInfo(slug: string): { event: PmEvent; market: PmMarket } | null {
    return this.known.get(slug) ?? null;
  }

  /** Look a market up by slug when nothing has shown it yet (a restored layout). */
  async resolveMarket(slug: string): Promise<{ event: PmEvent | null; market: PmMarket } | null> {
    const k = this.known.get(slug);
    if (k) return k;
    try {
      const market = await this.client.getMarket(slug);
      // Stubbed under a fake event so tick size and title stick, instead of being re-inferred from the book each time.
      const event: PmEvent = { slug: '', title: market.question || '', category: market.category, tags: [], teams: [], startTime: '', live: false, markets: [market] };
      this.known.set(slug, { event, market });
      return { event: null, market };
    } catch {
      return null;
    }
  }

  /** Apply a local order-list edit at once; confirmed against the venue shortly after, not on the 30s poll. */
  editOrders(fn: (orders: PmOrder[]) => PmOrder[]): void {
    this.account = { ...this.account, orders: fn([...this.account.orders]) };
    this.emit('account', this.account);
    if (this.confirmTimer !== null) clearTimeout(this.confirmTimer);
    this.confirmTimer = window.setTimeout(() => { this.confirmTimer = null; void this.pollAccount(); }, 1_500);
  }

  private confirmTimer: number | null = null;

  async pollAccount(): Promise<void> {
    if (!this.ws) return;
    const [bal, pos, ord] = await Promise.allSettled([
      this.client.getBalances(),
      this.client.getPositions(),
      this.client.getOpenOrders(),
    ]);
    const err = [bal, pos, ord].find((r): r is PromiseRejectedResult => r.status === 'rejected');
    const prev = this.account;
    this.account = {
      balance: bal.status === 'fulfilled' ? (bal.value.find((b) => b.currency === 'USD') ?? bal.value[0] ?? null) : prev.balance,
      positions: pos.status === 'fulfilled' ? pos.value : prev.positions,
      orders: ord.status === 'fulfilled' ? ord.value : prev.orders,
      ledger: prev.ledger,
      history: prev.history,
      at: err ? prev.at : Date.now(),
      error: err ? String((err.reason as Error)?.message ?? err.reason) : null,
    };
    if (err) logWarn('pm-feed', `account poll: ${this.account.error}`);
    this.emit('account', this.account);
  }

  private onFrame(frame: any): void {
    const md = frame?.marketData;
    if (!md) return; // heartbeats, acks, trades (not rendered yet)
    const b = parseBook(md);
    if (!b.slug || !this.subs.has(b.slug)) return;
    this.lastFrameAt.set(b.slug, Date.now());
    this.books.set(b.slug, b);
    this.emit('book', b);
  }

  // Private-frame envelope keys are matched loosely, so a venue rename degrades to "ignored", never a crash.
  private seenFrameShapes = new Set<string>();

  private onPrivateFrame(frame: any): void {
    if (!frame || typeof frame !== 'object') return;
    let handled = false;
    for (const [key, val] of Object.entries(frame as Record<string, any>)) {
      if (!val || typeof val !== 'object') continue;
      const k = key.toLowerCase();
      handled = true;
      if (k.includes('order') && Array.isArray(val.orders)) {
        // The open-order snapshot: the venue's list is the list.
        const orders = (val.orders as any[]).map(parseOrder).filter((o) => o.id);
        this.account = { ...this.account, orders, at: Date.now(), error: null };
        this.emit('account', this.account);
      } else if (k.includes('order') && val.execution) {
        this.applyExecution(parseExecution(val.execution));
      } else if (k.includes('position') && (val.afterPosition || val.position)) {
        const after = val.afterPosition ?? val.position;
        const slug = String(val.marketSlug ?? after?.marketSlug ?? after?.marketMetadata?.slug ?? '');
        if (slug) this.applyPositionDelta(slug, after);
      } else if (k.includes('balance') && (val.afterBalance || val.currentBalance !== undefined)) {
        const b = val.afterBalance ?? val;
        const balanceCash = dollarsToCash(b?.currentBalance?.value ?? b?.currentBalance) ?? this.account.balance?.balanceCash ?? 0;
        const buyingPowerCash = dollarsToCash(b?.buyingPower?.value ?? b?.buyingPower) ?? this.account.balance?.buyingPowerCash ?? balanceCash;
        this.account = {
          ...this.account,
          balance: { ...(this.account.balance ?? { currency: 'USD', openOrdersCash: 0, unsettledCash: 0 }), currency: String(b?.currency ?? 'USD'), balanceCash, buyingPowerCash },
          at: Date.now(),
        };
        this.emit('account', this.account);
      } else {
        handled = false;
      }
    }
    if (!handled) {
      // Logged once per unmatched shape, since docs describe channels by fields, not envelope names.
      const shape = Object.keys(frame).map((k) => `${k}:${frame[k] && typeof frame[k] === 'object' ? Object.keys(frame[k]).slice(0, 6).join('/') : typeof frame[k]}`).join(' ');
      if (!this.seenFrameShapes.has(shape)) {
        this.seenFrameShapes.add(shape);
        logWarn('pm-feed', `private frame not recognised: ${shape.slice(0, 300)}`);
      }
    }
  }

  /** Rebuilds realised P&L from trade history; run at start and briefly after a fill since activities lag the stream. */
  refreshLedger(): Promise<void> {
    if (this.ledgerInFlight) return this.ledgerInFlight;
    this.ledgerInFlight = (async () => {
      try {
        const { fills, resolutions } = await this.client.getActivities();
        this.account = { ...this.account, ledger: buildLedger(fills, resolutions), history: { fills, resolutions } };
        this.emit('account', this.account);
      } catch (e) {
        logWarn('pm-feed', `trade history: ${(e as Error).message}`);
      } finally {
        this.ledgerInFlight = null;
      }
    })();
    return this.ledgerInFlight;
  }

  private scheduleLedger(delayMs = 1_200): void {
    if (this.ledgerTimer !== null) clearTimeout(this.ledgerTimer);
    this.ledgerTimer = window.setTimeout(() => { this.ledgerTimer = null; void this.refreshLedger(); }, delayMs);
  }

  /** An execution replaces its order in the list, or removes it when nothing is left. */
  private applyExecution(ex: PmExecution): void {
    const o = ex.order;
    if (!o.id) return;
    const done = /CANCELED|CANCELLED|REJECTED|EXPIRED|FILLED|DONE_FOR_DAY/.test(ex.type) && o.leavesMq <= 0
      || /CANCELED|CANCELLED|REJECTED|EXPIRED|REPLACED/.test(o.state) || (o.state.endsWith('FILLED') && !o.state.includes('PARTIALLY'));
    const rest = this.account.orders.filter((x) => x.id !== o.id);
    this.account = { ...this.account, orders: done ? rest : [...rest, o], at: Date.now() };
    this.emit('execution', ex);
    this.emit('account', this.account);
    if (/FILL/.test(ex.type)) this.scheduleLedger();
  }

  /** Position deltas carry no mark; keep the last mark (or book mid) and schedule a full read to true it up. */
  private applyPositionDelta(slug: string, after: any): void {
    const d = parsePositionDelta(slug, after);
    const prev = this.account.positions.find((p) => p.slug === slug);
    const book = this.books.get(slug);
    const mid = book && book.bids[0] && book.offers[0] ? Math.round((book.bids[0].priceMc + book.offers[0].priceMc) / 2) : null;
    const markMc = prev?.markMc ?? mid;
    const cashValueCash = markMc !== null ? d.netMq * markMc : prev?.cashValueCash ?? d.costCash;
    const next: PmPosition = {
      slug,
      title: prev?.title ?? this.known.get(slug)?.market.title ?? slug,
      outcome: prev?.outcome ?? '',
      netMq: d.netMq,
      costCash: d.costCash,
      realizedCash: d.realizedCash ?? prev?.realizedCash ?? 0,
      cashValueCash,
      avgMc: d.netMq ? Math.round(Math.abs(d.costCash / d.netMq)) : null,
      markMc,
      unrealizedCash: cashValueCash - d.costCash,
      expired: prev?.expired ?? false,
    };
    const positions = [...this.account.positions.filter((p) => p.slug !== slug), ...(d.netMq !== 0 || prev ? [next] : [])];
    this.account = { ...this.account, positions, at: Date.now() };
    this.emit('account', this.account);
    if (this.confirmTimer !== null) clearTimeout(this.confirmTimer);
    this.confirmTimer = window.setTimeout(() => { this.confirmTimer = null; void this.pollAccount(); }, 1_500);
  }
}

/** The event slug in a polymarket.us URL, or a bare slug; null for ordinary words. */
export function slugFromQuery(q: string): string | null {
  const url = q.match(/polymarket\.us\/(?:[a-z0-9-]+\/)*([a-z0-9][a-z0-9-]*)\/?(?:[?#].*)?$/i);
  if (url) return url[1].toLowerCase();
  if (/^[a-z0-9]+(?:-[a-z0-9]+){2,}$/i.test(q) && /\d/.test(q)) return q.toLowerCase();
  return null;
}
